import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { defaultHittingMonitorFilter, DatePicker, Button, CheckboxDropdown, HittingMonitor, PERSPECTIVE_TYPES, LoadingSpinner } from 'yankees-react-utils';
import styled from 'styled-components';
import { useOktaQuery, PlayerReport, usePitchVideoFn, useMultiVideoFn, Stats } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import { VideoPlayerModal } from '../../../components/VideoPlayer';
import useConfig from '../../../utils/useConfig';

const getLevelOptions = (battingSeasons) => {
    const extra = [{ Abbr: 'All', LevelID: null, SortOrder: 0 }, { Abbr: 'MIN', LevelID: 7, SortOrder: 2 }];

    const levels = battingSeasons.map((bs) => bs.Level);

    return extra.concat([...new Set(levels.map((l) => l.LevelID))].map((levelId) => levels.find((l) => l.LevelID === levelId)))
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((level) => ({
            name: level.Abbr,
            value: level.LevelID,
        }));
};

const StyledCheckboxDropdown = styled(CheckboxDropdown)`
    .dropdown-toggle {
        width: 90px;
    }
`;

const MatchupReportHitMon = ({ batterId, levelId, currentYear, isLHB, isLHP }) => {
    const { t: translate } = useTranslation();
    const { UsePreviousYearDataHittingMonitor } = useConfig();
    const defaultStartDate = window.$IsDugoutApp
        ? `${currentYear - (UsePreviousYearDataHittingMonitor ? 1 : 0)}-01-01`
        : new Date(currentYear - (UsePreviousYearDataHittingMonitor ? 1 : 0), 0, 1);
    const defaultEndDate = window.$IsDugoutApp ? `${currentYear}-12-31` : new Date();
    const [levels, setLevels] = useState([]);
    const [levelsChanged, setLevelsChanged] = useState([]);
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [startDateChanged, setStartDateChanged] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [endDateChanged, setEndDateChanged] = useState(defaultEndDate);
    const [atbatCount, setAtbatCount] = useState(0);

    const { isFetching: isFetchingBattingSeasons } = useOktaQuery(
        ['battingSeasons', batterId],
        (t) => Stats.seasonBatting(t, { playerid: batterId }),
        {
            onSuccess: (data) => setLevels(getLevelOptions(data).map((lo) => ({
                ...lo,
                checked: lo.value === levelId || (levelId === 8 && lo.value === 21),
            }))),
            enabled: !window.$IsDugoutApp,
        },
    );

    const [filter, setFilter] = useState({
        ...defaultHittingMonitorFilter,
        batterHand: isLHB ? 2 : 1,
        pitcherHand: isLHP ? 2 : 1,
    });

    // Do not send yearid or the dates will be ignored!
    const { data: hitmonData, isFetching: isLoadingHitMon } = useOktaQuery(
        ['HitMon', { batterId, startDate, endDate, levelids: levels?.filter((l) => l.checked).map((l) => l.value).join(',') }],
        (t) => PlayerReport.hitmonPitches(t, {
            playerid: batterId,
            levelids: window.$IsDugoutApp ? [levelId] : levels?.filter((l) => l.checked).map((l) => l.value).filter((x) => !!x),
            startdate: startDate,
            enddate: endDate,
        }),
        {
            enabled: !isFetchingBattingSeasons,
            onSuccess: (data) => {
                const lastxpas = data?.pitches?.sort((a, b) => (b?.lastxpa) - (a?.lastxpa))[0]?.lastxpa ?? 100;
                setAtbatCount(lastxpas);
                setFilter({ ...filter, lastPAs: lastxpas });
            },
        },
    );

    useEffect(() => {
        setFilter((f) => ({
            ...f,
            pitcherHand: isLHP ? 2 : 1,
        }));
    }, [isLHP]);

    useEffect(() => {
        setFilter((f) => ({
            ...f,
            batterHand: isLHB ? 2 : 1,
        }));
    }, [isLHB]);

    useEffect(() => { setLevelsChanged(levels); }, [levels]);

    const videoPlayerModalContainer = useRef();
    const pitchVideoFn = usePitchVideoFn();
    const multiVideoFn = useMultiVideoFn();

    const applyFilter = () => {
        setLevels(levelsChanged);
        setStartDate(startDateChanged);
        setEndDate(endDateChanged);
    };

    const onLevelDropdownChange = (o, key, e) => {
        if (key === 'All' && e.target.checked) {
            setLevelsChanged(levelsChanged.map((l, i) => ({
                ...l,
                checked: !i,
            })));
        } else {
            const newLevels = levelsChanged.map((lo) => ({
                ...lo,
                checked: lo.name === o.name ? e.target.checked : !!lo.checked,
            }));
            if (newLevels[0].checked && newLevels.filter((l) => !!l.checked).length > 1) {
                newLevels[0].checked = false;
            } else if (!newLevels.filter((l) => !!l.checked).length) {
                newLevels[0].checked = true;
            }

            setLevelsChanged(newLevels);
        }
    };

    const selectedLevels = levelsChanged.filter((l) => !!l.checked);
    const levelPlaceholder = selectedLevels.length === 1 ? selectedLevels[0].name : `${selectedLevels.length} ${translate('baseball.level_other')}`;

    if (isFetchingBattingSeasons || isLoadingHitMon) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <div ref={videoPlayerModalContainer} />
            <ReportCard>
                <div className="d-flex justify-content-between align-items-center mb-2 position-relative" style={{ zIndex: 10 }}>
                    <h4>{translate('sections.opponent.player.hitting-monitor')}</h4>
                    {!window.$IsDugoutApp && (
                        <div className="d-flex align-items-center">
                            <StyledCheckboxDropdown
                                value={levelsChanged}
                                onChange={onLevelDropdownChange}
                                options={levelsChanged}
                                titleOverrideFormat
                                title={levelPlaceholder}
                                className="me-3"
                            />
                            <DatePicker
                                size="sm"
                                maxDate={endDateChanged}
                                value={startDateChanged}
                                onSelect={setStartDateChanged}
                                openToDate={startDateChanged}
                            />
                            <span className="mx-2">{translate('common.to')}</span>
                            <DatePicker
                                size="sm"
                                minDate={startDateChanged}
                                value={endDateChanged}
                                onSelect={setEndDateChanged}
                                openToDate={endDateChanged}
                            />
                            <Button className="ms-2" variant="primary" onClick={applyFilter}>{translate('common.filter')}</Button>
                        </div>
                    ) }
                </div>
                <HittingMonitor
                    isLoading={isLoadingHitMon}
                    initialPerspective={PERSPECTIVE_TYPES.pitcher}
                    liveFilter={filter}
                    setLiveFilter={setFilter}
                    pitches={hitmonData?.pitches ?? []}
                    pitchVideoFn={pitchVideoFn}
                    multiVideoFn={multiVideoFn}
                    videoPlayerModal={VideoPlayerModal}
                    modalContainer={videoPlayerModalContainer}
                    hidePerspectiveToggle
                    hideEvLaChart
                    hidePitcherHand
                    hideBatterHand
                    maxPAs={atbatCount}
                    enablePlayAllInStatsTable={!window.$IsDugoutApp}
                />
            </ReportCard>
        </>
    );
};

MatchupReportHitMon.propTypes = {
    currentYear: PropTypes.number.isRequired,
    levelId: PropTypes.number,
    batterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isLHB: PropTypes.bool.isRequired,
    isLHP: PropTypes.bool.isRequired,
};
MatchupReportHitMon.defaultProps = {
    levelId: 8,
};

export default MatchupReportHitMon;
